// Brand → domain map for app logos. The catalog's stored login_url is usually
// an Entra/SSO endpoint (so faviconing it yields the Microsoft logo or a
// generic globe), and homepage_url is often missing — so for recognizable
// apps we map the canonical name to the real brand domain and favicon that.
// Keyed by normalized name (lowercase, alphanumeric only). Long-tail apps
// fall back to homepage_url, then a letter tile (see AppLogo).

export const LOGO_DOMAINS: Record<string, string> = {
  // Productivity / collaboration
  slack: 'slack.com',
  figma: 'figma.com',
  notion: 'notion.so',
  atlassianjiraconfluence: 'atlassian.com',
  jira: 'atlassian.com',
  confluence: 'atlassian.com',
  zoom: 'zoom.us',
  asana: 'asana.com',
  mondaycom: 'monday.com',
  smartsheet: 'smartsheet.com',
  airtable: 'airtable.com',
  miro: 'miro.com',
  lucidchart: 'lucid.co',
  lucidspark: 'lucid.co',
  canva: 'canva.com',
  googleworkspace: 'google.com',
  microsoft365: 'microsoft.com',
  microsoftazure: 'azure.microsoft.com',
  shortcut: 'shortcut.com',
  guidde: 'guidde.com',
  tango: 'tango.us',
  robin: 'robinpowered.com',
  pingboardworkleap: 'workleap.com',
  pingboard: 'pingboard.com',
  vimcal: 'vimcal.com',
  superhuman: 'superhuman.com',
  // AI
  chatgpt: 'openai.com',
  claude: 'claude.ai',
  perplexity: 'perplexity.ai',
  grok: 'x.ai',
  harveyai: 'harvey.ai',
  gammaai: 'gamma.app',
  wisprflow: 'wisprflow.ai',
  flow: 'wisprflow.ai',
  superwhisper: 'superwhisper.com',
  turboai: 'turbo.ai',
  glean: 'glean.com',
  lovable: 'lovable.dev',
  gridmonitor: 'gridmonitor.com',
  infrastructureinvestors: 'infrastructureinvestor.com',
  primaverap6: 'oracle.com',
  tarascd: 'powergem.com',
  // Dev / infra
  github: 'github.com',
  githubenterprise: 'github.com',
  gitlab: 'gitlab.com',
  datadog: 'datadoghq.com',
  grafana: 'grafana.com',
  snowflake: 'snowflake.com',
  snowflakeintelligence: 'snowflake.com',
  tableau: 'tableau.com',
  tableaucloud: 'tableau.com',
  retool: 'retool.com',
  vercel: 'vercel.com',
  netlify: 'netlify.com',
  digitalocean: 'digitalocean.com',
  googlecloudplatform: 'cloud.google.com',
  oraclecloudinfrastructureconsole: 'oracle.com',
  pagerduty: 'pagerduty.com',
  dbtlabs: 'getdbt.com',
  airflow: 'airflow.apache.org',
  n8n: 'n8n.io',
  workato: 'workato.com',
  auvik: 'auvik.com',
  pingdom: 'pingdom.com',
  zabbix: 'zabbix.com',
  zabbixminingops: 'zabbix.com',
  splunk: 'splunk.com',
  teleport: 'goteleport.com',
  tailscale: 'tailscale.com',
  tailscalevpn: 'tailscale.com',
  warp: 'warp.dev',
  // Security
  vanta: 'vanta.com',
  scytaleai: 'scytale.ai',
  beauceronsecurity: 'beauceronsecurity.com',
  bitwarden: 'bitwarden.com',
  zerofox: 'zerofox.com',
  fortigate: 'fortinet.com',
  trendmicro: 'trendmicro.com',
  nessus: 'tenable.com',
  // Finance / HR / legal
  netsuite: 'netsuite.com',
  brex: 'brex.com',
  brexdashboard: 'brex.com',
  billcom: 'bill.com',
  quickbooksonline: 'quickbooks.intuit.com',
  expensify: 'expensify.com',
  navan: 'navan.com',
  carta: 'carta.com',
  rippling: 'rippling.com',
  ripplingsso: 'rippling.com',
  ripplingexchangeapplication: 'rippling.com',
  dayforce: 'dayforce.com',
  greenhouse: 'greenhouse.io',
  checkr: 'checkr.com',
  checr: 'checkr.com',
  ironclad: 'ironcladapp.com',
  docusign: 'docusign.com',
  docsend: 'docsend.com',
  docusignhellosign: 'hellosign.com',
  hellosigndropboxsign: 'dropbox.com',
  dropboxsignhellosign: 'dropbox.com',
  litera: 'litera.com',
  vena: 'venasolutions.com',
  macabacus: 'macabacus.com',
  bloomberg: 'bloomberg.com',
  bloombergterminal: 'bloomberg.com',
  bloomberganywhere: 'bloomberg.com',
  factset: 'factset.com',
  auditboard: 'auditboard.com',
  practicallaw: 'thomsonreuters.com',
  salesforce: 'salesforce.com',
  servicenow: 'servicenow.com',
  zoominfo: 'zoominfo.com',
  apollo: 'apollo.io',
  linkedinrecruiter: 'linkedin.com',
  linkedin: 'linkedin.com',
  indeed: 'indeed.com',
  mailchimp: 'mailchimp.com',
  twiliosendgrid: 'sendgrid.com',
  sendgrid: 'sendgrid.com',
  // Misc / ops
  adobe: 'adobe.com',
  acrobat: 'adobe.com',
  creativecloud: 'adobe.com',
  verkadacommand: 'verkada.com',
  spellbook: 'spellbook.legal',
  xakia: 'xakiatech.com',
  odoo: 'odoo.com',
  shipstation: 'shipstation.com',
  siteground: 'siteground.com',
  godaddy: 'godaddy.com',
  webflow: 'webflow.com',
  starlink: 'starlink.com',
  teamviewer: 'teamviewer.com',
  procore: 'procore.com',
  autocad: 'autodesk.com',
  revizto: 'revizto.com',
  patchmypc: 'patchmypc.com',
  diagramsnet: 'diagrams.net',
  obsidianimporter: 'obsidian.md',
  granola: 'granola.ai',
  navanexpensecalendarconnect: 'navan.com',
  rogers: 'rogers.com',
  verizonbusiness: 'verizon.com',
  spectrumenterprise: 'spectrum.com',
  vmware: 'vmware.com',
  vmwarecloud: 'vmware.com',
  purestorage: 'purestorage.com',
  veeam: 'veeam.com',
  meraki: 'meraki.com',
  redhat: 'redhat.com',
  applebusinessmanager: 'apple.com',
  '360learning': '360learning.com',
  elementarydata: 'elementary-data.com',
  blackapp: 's3partners.com',
  // extended coverage (in-use apps that fell to letter tiles)
  acres: 'acres.io',
  amazonbusinessprime: 'amazon.com',
  arcgis: 'esri.com',
  arin: 'arin.net',
  attbusiness: 'att.com',
  beanworks: 'beanworks.com',
  ccrcorp: 'ccrcorp.com',
  clearviewconnectsboth: 'clearviewconnects.com',
  conga: 'conga.com',
  deltek: 'deltek.com',
  dfinvdr: 'dfinsolutions.com',
  dunbradstreet: 'dnb.com',
  enteliweb: 'deltacontrols.com',
  fiberlocator: 'fiberlocator.com',
  godaddycanada: 'godaddy.com',
  googlefi: 'fi.google.com',
  hotwire: 'hotwirecommunications.com',
  influx: 'influxdata.com',
  kaleidoscope: 'kaleidoscope.app',
  landglide: 'landglide.com',
  leadforensics: 'leadforensics.com',
  meritnetwork: 'merit.edu',
  microsoftserver: 'microsoft.com',
  nasdaq: 'nasdaq.com',
  nasdaqir: 'nasdaq.com',
  nrgstream: 'nrgstream.com',
  onxmaps: 'onxmaps.com',
  orennia: 'orennia.com',
  peacock: 'peacocktv.com',
  psse: 'siemens.com',
  radb: 'radb.net',
  revu: 'bluebeam.com',
  skmpowertools: 'skm.com',
  squareworksautomate: 'squareworks.com',
  stepsaver: 'stepsaverdata.com',
  tdsynnexcanadaulc: 'tdsynnex.com',
  teragonetworks: 'terago.ca',
  thereceptionist: 'thereceptionist.com',
  thinkcell: 'think-cell.com',
  unifiedfabricmanager: 'nvidia.com',
  visoid: 'visoid.com',
  wex: 'wexinc.com',
  yesenergy: 'yesenergy.com',
  zerto: 'zerto.com',
};

const SSO_HOSTS = [
  'login.microsoftonline.com',
  'myapplications.microsoft.com',
  'account.activedirectory',
  'sts.windows.net',
  'login.windows.net',
];

export function logoDomain(app: {
  app_name: string;
  display_name?: string | null;
  homepage_url?: string | null;
  login_url?: string | null;
}): string | null {
  const norm = (s: string) =>
    s
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  const mapped = LOGO_DOMAINS[norm(app.display_name ?? '')] || LOGO_DOMAINS[norm(app.app_name)];
  if (mapped) return mapped;
  // Fall back to the homepage host — but never an SSO endpoint (that's how
  // the Microsoft logo leaked onto Rippling/Robin etc.).
  for (const raw of [app.homepage_url, app.login_url]) {
    if (!raw) continue;
    try {
      const host = new URL(raw.startsWith('http') ? raw : `https://${raw}`).hostname;
      if (!SSO_HOSTS.some((h) => host.includes(h))) return host;
    } catch {
      /* ignore */
    }
  }
  return null;
}
